import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import { FormsModule } from '@angular/forms'
import { CardModule } from 'primeng/card'
import { ButtonModule } from 'primeng/button'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { MultiSelectModule } from 'primeng/multiselect'
import { TooltipModule } from 'primeng/tooltip'
import * as L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import '@geoman-io/leaflet-geoman-free'
// Le CSS de geoman est chargé globalement via angular.json (le webpack custom
// n'applique pas de loader CSS aux fichiers de node_modules).
import { SectorService } from '../../services/sector.service'
import {
  SectorGeometry,
  SectorModel,
  SectorPayload,
} from '../../../../core/models/sector.model'
import { UserService } from '../../../users/services/user.service'
import { ToastService } from '../../../../shared/services/toast.service'
import { getSectorBounds } from '../../../../core/tools/sector-tools'

type EditMode = 'idle' | 'creating' | 'editing'

/** Domicile d'un technicien à afficher sur la carte. */
interface TechnicianHome {
  id: number
  initials: string
  fullName: string
  color: string | null
  latitude: number
  longitude: number
}

const DEFAULT_COLOR = '#3b82f6'
// Palette proposée pour distinguer les secteurs sur la carte
const PALETTE = [
  '#3b82f6', // bleu
  '#2563eb', // bleu foncé
  '#0ea5e9', // cyan
  '#14b8a6', // sarcelle
  '#10b981', // émeraude
  '#22c55e', // vert
  '#84cc16', // lime
  '#eab308', // jaune
  '#f59e0b', // ambre
  '#f97316', // orange
  '#ef4444', // rouge
  '#dc2626', // rouge foncé
  '#ec4899', // rose
  '#d946ef', // fuchsia
  '#a855f7', // violet
  '#8b5cf6', // pourpre
  '#6366f1', // indigo
  '#78716c', // pierre
  '#64748b', // ardoise
  '#1e293b', // anthracite
]

@Component({
  selector: 'app-sector-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ButtonModule,
    ProgressSpinnerModule,
    MultiSelectModule,
    TooltipModule,
  ],
  templateUrl: './sector-settings.component.html',
  styleUrl: './sector-settings.component.scss',
})
export class SectorSettingsComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mapContainer', { static: true })
  mapContainer!: ElementRef<HTMLDivElement>

  private sectorService = inject(SectorService)
  private userService = inject(UserService)
  private toast = inject(ToastService)
  private http = inject(HttpClient)

  loading = true
  saving = false
  sectors: SectorModel[] = []
  /** Techniciens sélectionnables ({ label, value }) renvoyés par /users/select. */
  technicians: { label: string; value: number }[] = []
  /** Domiciles des techniciens (avec coordonnées) affichés sur la carte. */
  technicianHomes: TechnicianHome[] = []
  /** Affiche / masque les points domicile des techniciens. */
  showHomes = true
  /** Affiche / masque les limites de départements. */
  showDepartments = true

  mode: EditMode = 'idle'
  selectedId: number | null = null

  // Brouillon en cours (création ou édition)
  draftName = ''
  draftColor = DEFAULT_COLOR
  draftUserIds: number[] = []
  palette = PALETTE

  private map!: L.Map
  private resizeObserver: ResizeObserver | null = null
  /** Couche affichée par secteur existant (clé = id du secteur). */
  private layerById = new Map<number, L.Polygon>()
  /** Couche en cours de dessin / édition (non encore persistée ou en édition). */
  private draftLayer: L.Polygon | null = null
  /** Couche regroupant les marqueurs de domicile des techniciens. */
  private homesLayer = L.layerGroup()
  /** Couche des limites de départements (fond GeoJSON france.json). */
  private departmentsLayer: L.GeoJSON | null = null
  /** GeoJSON des départements, mis en cache après le premier chargement. */
  private departmentsGeo: unknown = null

  ngAfterViewInit(): void {
    this.initMap()
    this.loadDepartments()
    this.loadSectors()
    this.loadTechnicians()
    this.loadTechnicianHomes()
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect()
    this.map?.remove()
  }

  // --- Carte ---

  private initMap(): void {
    // Limite la carte à la France métropolitaine (mêmes bornes que la carte du Programmator).
    const franceBounds = L.latLngBounds(
      [41.3, -5.5], // Sud-Ouest (Pyrénées / Atlantique)
      [51.2, 9.8] // Nord-Est (Nord / Alsace)
    )

    this.map = L.map(this.mapContainer.nativeElement, {
      center: [46.6, 2.4], // centre de la France métropolitaine
      zoom: 6,
      minZoom: 5,
      maxBounds: franceBounds,
      maxBoundsViscosity: 1.0,
    })

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '© OpenStreetMap, © CartoDB',
      subdomains: 'abcd',
      maxZoom: 19,
    }).addTo(this.map)

    // Pane dédié aux limites de départements : sous les polygones de secteur
    // (overlayPane = 400) mais au-dessus des tuiles, pour servir de simple fond.
    const depPane = this.map.createPane('departments')
    depPane.style.zIndex = '350'
    depPane.style.pointerEvents = 'none'

    // Calque des domiciles techniciens (affiché au-dessus des secteurs).
    this.homesLayer.addTo(this.map)

    // La carte est initialisée avant que son conteneur flex ait sa taille finale :
    // on force Leaflet à recalculer ses dimensions une fois le layout appliqué.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => this.map.invalidateSize())
    )
    setTimeout(() => this.map.invalidateSize(), 300)

    // Filet de sécurité : recalcule la taille à chaque redimensionnement du conteneur
    // (couvre les cas où la hauteur n'est résolue qu'après le premier rendu).
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.map.invalidateSize())
      this.resizeObserver.observe(this.mapContainer.nativeElement)
    }

    // Barre d'outils geoman : on n'expose que le dessin de polygone (piloté par nos boutons).
    this.map.pm.setLang('fr')
    this.map.pm.setGlobalOptions({ snappable: true })

    // Fin du dessin d'un nouveau polygone
    this.map.on('pm:create', (e: any) => {
      const layer = e.layer as L.Polygon
      // On retire la couche brute créée par geoman : on la gère nous-mêmes comme draftLayer
      this.map.removeLayer(layer)
      this.draftLayer = layer
      layer.setStyle?.({ color: this.draftColor, fillColor: this.draftColor })
      layer.addTo(this.map)
      layer.pm.enable({ allowSelfIntersection: false })
      this.map.pm.disableDraw()
    })
  }

  // --- Données ---

  private loadSectors(): void {
    this.loading = true
    this.sectorService.getAll().subscribe({
      next: (sectors) => {
        this.sectors = sectors
        this.renderSectors()
        this.loading = false
      },
      error: () => {
        this.toast.showError('Erreur lors du chargement des secteurs')
        this.loading = false
      },
    })
  }

  private loadTechnicians(): void {
    this.userService.getForSelect().subscribe({
      next: (technicians) => {
        this.technicians = technicians
      },
      error: () => {
        this.toast.showError('Erreur lors du chargement des techniciens')
      },
    })
  }

  private loadDepartments(): void {
    // france.json est servi depuis public/ (cf. heat-map). Features : { code, nom }.
    this.http.get('france.json').subscribe({
      next: (geo) => {
        this.departmentsGeo = geo
        this.renderDepartments()
      },
      error: () => {
        this.toast.showError('Erreur lors du chargement des départements')
      },
    })
  }

  private renderDepartments(): void {
    if (this.departmentsLayer) {
      this.map.removeLayer(this.departmentsLayer)
      this.departmentsLayer = null
    }
    if (!this.showDepartments || !this.departmentsGeo) return

    this.departmentsLayer = L.geoJSON(this.departmentsGeo as any, {
      pane: 'departments',
      interactive: false,
      style: {
        color: '#94a3b8',
        weight: 1,
        opacity: 0.6,
        fill: false,
      },
    }).addTo(this.map)
  }

  toggleDepartments(): void {
    this.showDepartments = !this.showDepartments
    this.renderDepartments()
  }

  private loadTechnicianHomes(): void {
    this.userService.getUsersWithFilters({}).subscribe({
      next: (users) => {
        this.technicianHomes = users
          .filter((u) => u.homeLatitude != null && u.homeLongitude != null)
          .map((u) => ({
            id: u.id,
            initials: u.initials,
            fullName: u.fullName,
            color: u.color,
            latitude: u.homeLatitude,
            longitude: u.homeLongitude,
          }))
        this.renderTechnicianHomes()
      },
      error: () => {
        this.toast.showError('Erreur lors du chargement des domiciles techniciens')
      },
    })
  }

  private renderTechnicianHomes(): void {
    this.homesLayer.clearLayers()
    if (!this.showHomes) return

    for (const home of this.technicianHomes) {
      const color = home.color || '#1e293b'
      const icon = L.divIcon({
        className: 'tech-home-marker',
        html: `<div class="tech-home-pin" style="--home-color:${color}">
          <span>${this.escapeHtml(home.initials)}</span>
        </div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
      })

      L.marker([home.latitude, home.longitude], {
        icon,
        // Au-dessus des polygones de secteur mais sans capter les clics d'édition.
        interactive: true,
      })
        .bindTooltip(`Domicile — ${this.escapeHtml(home.fullName)}`, {
          direction: 'top',
        })
        .addTo(this.homesLayer)
    }
  }

  toggleHomes(): void {
    this.showHomes = !this.showHomes
    this.renderTechnicianHomes()
  }

  private escapeHtml(value: string): string {
    return (value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  private renderSectors(): void {
    // Nettoyer les couches existantes
    this.layerById.forEach((layer) => this.map.removeLayer(layer))
    this.layerById.clear()

    for (const sector of this.sectors) {
      const layer = this.geometryToLayer(sector.geometry, sector.color)
      if (!layer) continue
      // Étiquette permanente affichée au centre de la zone coloriée.
      layer.bindTooltip(sector.name, {
        permanent: true,
        direction: 'center',
        className: 'sector-label',
        opacity: 1,
      })
      layer.on('click', () => {
        if (this.mode === 'idle') this.startEdit(sector)
      })
      layer.addTo(this.map)
      this.layerById.set(sector.id, layer)
    }
  }

  private geometryToLayer(
    geometry: SectorGeometry,
    color: string | null
  ): L.Polygon | null {
    const ring = geometry?.coordinates?.[0]
    if (!ring || ring.length < 3) return null
    // GeoJSON = [lng, lat] → Leaflet = [lat, lng]
    const latlngs = ring.map(([lng, lat]) => [lat, lng] as [number, number])
    const c = color || DEFAULT_COLOR
    return L.polygon(latlngs, {
      color: c,
      fillColor: c,
      fillOpacity: 0.15,
      weight: 2,
    })
  }

  private layerToGeometry(layer: L.Polygon): SectorGeometry | null {
    const geojson = layer.toGeoJSON() as any
    const geometry = geojson?.geometry
    if (!geometry || geometry.type !== 'Polygon') return null
    return geometry as SectorGeometry
  }

  // --- Actions UI ---

  startCreate(): void {
    if (this.mode !== 'idle') this.cancel()
    this.mode = 'creating'
    this.selectedId = null
    this.draftName = ''
    this.draftColor = this.nextColor()
    this.draftUserIds = []
    this.map.pm.enableDraw('Polygon', {
      snappable: true,
      templineStyle: { color: this.draftColor },
      hintlineStyle: { color: this.draftColor, dashArray: [5, 5] },
      pathOptions: { color: this.draftColor, fillColor: this.draftColor },
    })
  }

  startEdit(sector: SectorModel): void {
    if (this.mode !== 'idle') this.cancel()
    this.mode = 'editing'
    this.selectedId = sector.id
    this.draftName = sector.name
    this.draftColor = sector.color || DEFAULT_COLOR
    this.draftUserIds = (sector.users ?? []).map((u) => u.id)

    // Masquer la couche statique et créer une couche éditable
    const existing = this.layerById.get(sector.id)
    if (existing) this.map.removeLayer(existing)

    const layer = this.geometryToLayer(sector.geometry, sector.color)
    if (layer) {
      layer.addTo(this.map)
      layer.pm.enable({ allowSelfIntersection: false })
      this.draftLayer = layer
      const bounds = getSectorBounds(sector.geometry)
      if (bounds) this.map.fitBounds(bounds, { padding: [40, 40] })
    }
  }

  onColorChange(color: string): void {
    this.draftColor = color
    this.draftLayer?.setStyle({ color, fillColor: color })
  }

  save(): void {
    const name = this.draftName.trim()
    if (!name) {
      this.toast.showError('Le nom du secteur est obligatoire')
      return
    }
    if (!this.draftLayer) {
      this.toast.showError('Veuillez dessiner le secteur sur la carte')
      return
    }
    const geometry = this.layerToGeometry(this.draftLayer)
    if (!geometry) {
      this.toast.showError('Géométrie du secteur invalide')
      return
    }

    const payload: SectorPayload = {
      name,
      color: this.draftColor,
      geometry,
      is_active: true,
      userIds: this.draftUserIds,
    }

    this.saving = true
    const request =
      this.mode === 'editing' && this.selectedId != null
        ? this.sectorService.update(this.selectedId, payload)
        : this.sectorService.create(payload)

    request.subscribe({
      next: (res) => {
        this.saving = false
        this.toast.showSuccess(res.message)
        this.resetDraft()
        this.loadSectors()
      },
      error: () => {
        this.saving = false
        this.toast.showError("Erreur lors de l'enregistrement du secteur")
      },
    })
  }

  remove(sector: SectorModel): void {
    if (!confirm(`Supprimer le secteur « ${sector.name} » ?`)) return
    this.sectorService.remove(sector.id).subscribe({
      next: (res) => {
        this.toast.showSuccess(res.message)
        if (this.selectedId === sector.id) this.resetDraft()
        this.loadSectors()
      },
      error: () => this.toast.showError('Erreur lors de la suppression'),
    })
  }

  cancel(): void {
    this.resetDraft()
    this.renderSectors()
  }

  private resetDraft(): void {
    this.map.pm.disableDraw()
    if (this.draftLayer) {
      this.draftLayer.pm.disable()
      this.map.removeLayer(this.draftLayer)
      this.draftLayer = null
    }
    this.mode = 'idle'
    this.selectedId = null
    this.draftName = ''
    this.draftColor = DEFAULT_COLOR
    this.draftUserIds = []
  }

  /** Couleur suivante non encore utilisée (sinon défaut). */
  private nextColor(): string {
    const used = new Set(this.sectors.map((s) => s.color))
    return this.palette.find((c) => !used.has(c)) ?? DEFAULT_COLOR
  }
}
